/**
 * Highlighted depth contours — a display-only emphasis. Tapping a depth contour
 * offers "Highlight", which adds that contour's charted depth value here; the
 * map then draws every isobath at that value in an accent colour so it stands
 * out while we pilot. This is purely visual: no alarm and no do-not-cross
 * meaning, which is what keeps it separate from a clearing depth. Kept in its
 * own small store so the map layer reads a clean list of values, the same shape
 * the clearing-depth highlight already uses.
 */
import { create } from 'zustand';

interface ContourHighlightState {
  /** Charted depth values (metres) currently emphasised on the chart. */
  values: number[];
  /** Turn the emphasis on or off for one contour value. */
  toggle: (valueM: number) => void;
  /** Whether a given contour value is currently emphasised. */
  isHighlighted: (valueM: number) => boolean;
  clear: () => void;
}

export const useContourHighlightStore = create<ContourHighlightState>((set, get) => ({
  values: [],
  toggle: (valueM) =>
    set((s) => ({
      values: s.values.includes(valueM)
        ? s.values.filter((v) => v !== valueM)
        : [...s.values, valueM],
    })),
  isHighlighted: (valueM) => get().values.includes(valueM),
  clear: () => set({ values: [] }),
}));
